import React from 'react';
import { Tag } from 'antd';
import { modes } from '@comall-backend-builder/types';
import './batch-detail-app.less';

/** 规则维度：1=SPU，2=品类+大类 */
const RULE_DIMENSION = {
    SPU: 1,
    CATEGORY: 2,
} as const;

const RULE_DIMENSION_LABEL: Record<number, string> = {
    [RULE_DIMENSION.SPU]: 'SPU',
    [RULE_DIMENSION.CATEGORY]: '品类+大类',
};

/**
 * /inside_show_attribute 返回的 rich_text_box 结构
 */
export type RichTextBoxValue = {
    ruleId?: string;
    ruleName?: string;
    richText?: string;
    ruleDimension?: number;
};

export type BatchDetailAppValue = {
    ruleName?: string;
    ruleCondition?: string;
    effectiveLabel?: string;
    content?: string;
};

/** 将 rich_text_box 转为展示结构 */
function normalizeBatchDetailApp(
    value: RichTextBoxValue | BatchDetailAppValue | null | undefined
): BatchDetailAppValue {
    if (!value) {
        return {};
    }

    const raw = value as RichTextBoxValue & BatchDetailAppValue;
    const dimensionLabel =
        raw.ruleDimension !== undefined && raw.ruleDimension !== null
            ? RULE_DIMENSION_LABEL[raw.ruleDimension]
            : undefined;

    return {
        ruleName: raw.ruleName,
        ruleCondition: raw.ruleCondition || dimensionLabel,
        effectiveLabel:
            raw.effectiveLabel || (dimensionLabel ? `批量配置 (${dimensionLabel})` : undefined),
        content: raw.content || raw.richText,
    };
}

/** 后台未返回有效 richTextBox 时不展示 */
function isBatchDetailAppEmpty(
    value: RichTextBoxValue | BatchDetailAppValue | null | undefined
): boolean {
    if (value == null) {
        return true;
    }
    if (typeof value === 'object' && Object.keys(value).length === 0) {
        return true;
    }
    const { ruleName, ruleCondition, effectiveLabel, content } = normalizeBatchDetailApp(value);
    return !ruleName && !ruleCondition && !effectiveLabel && !content;
}

function renderBatchDetailApp(
    value: RichTextBoxValue | BatchDetailAppValue | null | undefined = {},
    displayInfo: any = {}
) {
    if (isBatchDetailAppEmpty(value)) {
        return null;
    }

    const { className, style } = displayInfo;
    const { ruleName, ruleCondition, effectiveLabel, content } = normalizeBatchDetailApp(value);

    return (
        <div className={`batch-detail-app ${className || ''}`} style={style}>
            <div className="batch-detail-app__title">批量配置移动端详情 (只读)</div>
            <div className="batch-detail-app__row">
                <span className="batch-detail-app__label">命中规则：</span>
                <span className="batch-detail-app__value">{ruleName || '-'}</span>
                {ruleCondition ? (
                    <Tag className="batch-detail-app__tag" color="blue">
                        {ruleCondition}
                    </Tag>
                ) : null}
            </div>
            <div className="batch-detail-app__row">
                <span className="batch-detail-app__label">当前生效：</span>
                {effectiveLabel ? (
                    <Tag className="batch-detail-app__tag" color="blue">
                        {effectiveLabel}
                    </Tag>
                ) : (
                    <span className="batch-detail-app__value">-</span>
                )}
            </div>
            <div className="batch-detail-app__content">
                {content ? (
                    <div
                        className="batch-detail-app__content-html"
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                ) : (
                    <div className="batch-detail-app__content-empty">暂无批量配置详情</div>
                )}
            </div>
        </div>
    );
}

/**
 * 批量配置移动端详情（只读）展示
 * 数据来源：/inside_show_attribute 的 rich_text_box
 */
export class BatchDetailAppMode extends modes.ObjectMode {
    getDisplayComponent(
        value: RichTextBoxValue | BatchDetailAppValue | null | undefined,
        displayInfo: any
    ) {
        return renderBatchDetailApp(value, displayInfo);
    }

    getAvailableDisplayComponent(value: RichTextBoxValue | BatchDetailAppValue, displayInfo: any) {
        return renderBatchDetailApp(value, displayInfo);
    }

    getNotAvailableDisplayComponent(_displayInfo: any) {
        return null;
    }

    getControlComponent(controlInfo: any) {
        return renderBatchDetailApp(controlInfo.value, controlInfo);
    }
}
